import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  InputGroup,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import { createBankAccountTypePropertyShell } from '@finley-technologies/credit-agreement-utils';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import {
  CheckboxFormField,
  FormFieldLabel,
  InputFormField,
} from 'packages/common-frontend/components/FormField';
import { Select } from 'packages/common-frontend/components/Select';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

export const createBankAccountEntityShell = (): CreditAgreement.DomainEntity.Items.BankAccountEntity => ({
  entity: {
    id: uuidv4(),
    currencyCode: 'USD',
    name: 'name',
    institutionName: 'institutionName',
    routingNumber: '123456',
    accountNumber: '123456',
    bankInstitutionId: 'bankInstitutionId',
    implementationId: 'implementationId',
    bankInstitutionKey: 'xyzBank',
    organizationId: 'organizationId',
    // @jen add in this property that was required on BankAccount
    isActive: true,
  },
  type: createBankAccountTypePropertyShell('type'),
  _isWrittenIn_: true,
});

type EditBankAccountModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
  basePropertyKey: string;
  organizationId: string;
};
export const EditBankAccountModal = ({
  isOpen,
  setIsOpen,
  wipConfig: {
    formProps,
    organizationIdToBankAccountsMap,
    saveCreditAgreementFullArtifactWip,
  },
  basePropertyKey,
  organizationId,
}: EditBankAccountModalProps) => {
  const bankAccounts = organizationIdToBankAccountsMap[organizationId];
  const selectOptions = _.sortBy(
    _.map(bankAccounts, (ba) => ({
      value: ba.id,
      label: `${ba.institutionName} ${ba.name} rn: (**${ba.routingNumber.slice(
        -4,
      )}) an: (**${ba.accountNumber.slice(-4)})`,
    })),
    'label',
  );

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <ModalOverlay />
      <ModalContent padding="20px" minW="4xl">
        <ModalHeader>Edit Bank Account</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Flex alignItems="flex-end" flexDirection="row" mt={5}>
            <FormFieldLabel label="Bank Account" labelWidth="60" />
            <InputGroup width="100%">
              <Select
                value={getIn(formProps.values, `${basePropertyKey}.entity.id`)}
                onChange={(val) => {
                  const currentBankAccountEntity = getIn(
                    formProps.values,
                    basePropertyKey,
                  );
                  const properties = _.omit(currentBankAccountEntity, 'entity');
                  const bankAccountEntity = _.find(
                    organizationIdToBankAccountsMap[organizationId],
                    (bankAccount) => bankAccount.id === val,
                  );
                  const bankAccountEntityShell = createBankAccountEntityShell();
                  formProps.setFieldValue(`${basePropertyKey}`, {
                    ...bankAccountEntityShell,
                    entity: bankAccountEntity,
                    ...properties,
                  });
                }}
                options={selectOptions}
                width="xl"
              />
            </InputGroup>
          </Flex>
          <InputFormField
            propName={`${basePropertyKey}.section`}
            label="Section"
            formProps={formProps}
            value={getIn(formProps.values, `${basePropertyKey}.section`)}
            labelWidth="60"
            inputWidth="xl"
            flexDirection="row"
            alignItems="flex-end"
            marginTop={5}
          />
          <CheckboxFormField
            propName={`${basePropertyKey}._isWrittenIn_`}
            label="Is Written In"
            formProps={formProps}
            value={getIn(formProps.values, `${basePropertyKey}._isWrittenIn_`)}
            labelWidth="60"
            inputWidth="sm"
            flexDirection="row"
            marginTop={5}
          />

          <Flex justifyContent="center" mt="8">
            <Button
              onClick={() => {
                saveCreditAgreementFullArtifactWip({
                  variables: {
                    creditAgreementFullArtifactWip: {
                      id: formProps.values.id,
                      creditFacilityId: formProps.values.creditFacilityId,
                      creditAgreement: JSON.stringify(formProps.values),
                    },
                  },
                });
                setIsOpen(false);
              }}
              aria-label="Done"
              isDisabled={_.keys(formProps.errors).length > 0}
            >
              Done
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
